import React, { useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { getAuthSession } from "../auth.js";
import PageContext from "../PageContext.jsx";

const getFilenameFromDisposition = (disposition, fallbackExtension) => {
  const match = String(disposition || "").match(/filename="?([^"]+)"?/i);
  return match?.[1] || `findb-backup-${new Date().toISOString().replace(/[:.]/g, "-")}.${fallbackExtension}`;
};

const getErrorMessage = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.detail || "Could not create backup.";
    } catch {
      return "Could not create backup.";
    }
  }
  return data?.message || data?.detail || "Could not create backup.";
};

const backupDownloads = {
  sql: {
    endpoint: "/backups/download",
    extension: "sql",
    loadingLabel: "Creating SQL Backup...",
    successLabel: "SQL backup",
  },
  xls: {
    endpoint: "/backups/xls",
    extension: "xls",
    loadingLabel: "Creating XLS Backup...",
    successLabel: "XLS table export",
  },
  xlsReadable: {
    endpoint: "/backups/xls-readable",
    extension: "xls",
    loadingLabel: "Creating XLS Backup...",
    successLabel: "XLS table export with names",
  },
};

const Backups = () => {
  const session = getAuthSession();
  const isAdministrator = Boolean(session?.user?.IsAdministrator);
  const [loadingFormat, setLoadingFormat] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleDownload = async (format) => {
    const download = backupDownloads[format];
    if (!download) return;

    setLoadingFormat(format);
    setMessage("");
    setError("");

    try {
      const response = await apiClient.post(download.endpoint, null, {
        responseType: "blob",
      });
      const filename = getFilenameFromDisposition(response.headers?.["content-disposition"], download.extension);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage(`Downloaded ${download.successLabel}: ${filename}.`);
    } catch (err) {
      setError(await getErrorMessage(err));
    } finally {
      setLoadingFormat("");
    }
  };

  const isLoading = Boolean(loadingFormat);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Backups</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / Backups
          </p>
        </div>
      </header>

      <PageContext
        items={[
          { label: "Home", to: "/" },
          { label: "Backups" },
        ]}
        note="Create a full backend SQL backup, or export current-entity table records into an XLS file."
      />

      {isAdministrator && (
        <section className="panel page-cta">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Full Database</p>
              <h2>SQL Backup</h2>
            </div>
          </div>
          <p className="section-copy">
            Download a full SQL backup with table definitions and all table data. Use this file for database restore or migration work.
          </p>
          <div className="form-actions">
            <button className="btn" type="button" onClick={() => handleDownload("sql")} disabled={isLoading}>
              {loadingFormat === "sql" ? backupDownloads.sql.loadingLabel : "Download SQL Backup"}
            </button>
          </div>
        </section>
      )}

      <section className="panel page-cta">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Current Entity</p>
            <h2>XLS Table Data</h2>
          </div>
        </div>
        <p className="section-copy">
          Download current-entity records as an Excel-compatible XLS file, with each entity-scoped table on its own worksheet.
          Choose raw IDs, or names for reference fields while keeping each table primary key in column A.
        </p>
        <div className="form-actions">
          <button className="btn btn-secondary" type="button" onClick={() => handleDownload("xls")} disabled={isLoading || !isAdministrator}>
            {loadingFormat === "xls" ? backupDownloads.xls.loadingLabel : "Download XLS Table Data"}
          </button>
          <button className="btn btn-secondary" type="button" onClick={() => handleDownload("xlsReadable")} disabled={isLoading || !isAdministrator}>
            {loadingFormat === "xlsReadable" ? backupDownloads.xlsReadable.loadingLabel : "Download XLS With Names"}
          </button>
        </div>
      </section>

      <section>
        {!isAdministrator && (
          <p className="notice notice-error">Administrator access is required to download database backups.</p>
        )}
        {error && <p className="notice notice-error">{error}</p>}
        {message && <p className="notice notice-muted">{message}</p>}
      </section>
    </div>
  );
};

export default Backups;
